import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, CheckCircle2, Quote, Globe2, Calendar, Package, BarChart3 } from 'lucide-react';
import SiteLayout, { PALETTE } from './SiteLayout';
import SEO from '../../components/SEO';
import { ORG_JSONLD, SITE_URL } from '../../seo/landingConfig';

/**
 * Cashel Blue case study, full HTML SEO page (separate from the PDF
 * download at /case-study/cashel-blue). Google indexes this; the PDF is
 * linked as a downloadable artefact.
 */
export default function CashelBlueStoryPage() {
  const path = '/case-study/cashel-blue-story';
  const url = `${SITE_URL}${path}`;

  const articleJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: 'How Cashel Blue Farmhouse Cheesemakers replaced spreadsheets with Keystone',
    description:
      'A 40+ year old multi-award-winning Tipperary dairy with international distribution. How they moved batch traceability, dispatch and pallet labels off Excel and onto Keystone in 30 days.',
    image: `${SITE_URL}/customers/cashel-blue-logo.webp`,
    datePublished: '2026-02-01',
    dateModified: '2026-02-15',
    author: { '@type': 'Organization', name: 'CloudVoro' },
    publisher: {
      '@type': 'Organization',
      name: 'CloudVoro',
      logo: { '@type': 'ImageObject', url: `${SITE_URL}/brochure-assets/logos/keystone-horizontal-light-2048.png` },
    },
    mainEntityOfPage: { '@type': 'WebPage', '@id': url },
  };

  const breadcrumb = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: 'Home', item: SITE_URL },
      { '@type': 'ListItem', position: 2, name: 'Cashel Blue case study', item: url },
    ],
  };

  return (
    <SiteLayout>
      <SEO
        title="Cashel Blue Case Study, Excel to Keystone in 30 Days"
        description="How Cashel Farmhouse Cheesemakers replaced production spreadsheets with Keystone in 30 days, BRCGS-ready. Quotes used with written permission."
        path={path}
        keywords="Cashel Blue case study, Irish dairy software case study, food traceability case study Ireland, Cashel Farmhouse Cheesemakers Keystone, blue cheese production software"
        jsonLd={[ORG_JSONLD, articleJsonLd, breadcrumb]}
      />

      {/* HERO */}
      <section data-testid="cashel-story-hero" style={{ background: PALETTE.cream, padding: '110px 32px 70px', borderBottom: `1px solid ${PALETTE.border}` }}>
        <div style={{ maxWidth: 1080, margin: '0 auto' }}>
          <nav aria-label="Breadcrumb" style={{ fontSize: 12, color: PALETTE.muted, marginBottom: 22 }}>
            <Link to="/" style={{ color: PALETTE.muted, textDecoration: 'none' }}>Home</Link>
            <span style={{ margin: '0 8px', color: PALETTE.border }}>/</span>
            <span style={{ color: PALETTE.ink, fontWeight: 600 }}>Cashel Blue case study</span>
          </nav>

          <div style={{ fontSize: 11, letterSpacing: '0.24em', textTransform: 'uppercase', fontWeight: 700, color: PALETTE.magenta, marginBottom: 18 }}>
            Founding customer · Live since February 2026
          </div>
          <h1 style={{ fontFamily: "'Playfair Display', Georgia, serif", fontSize: 'clamp(34px, 4.6vw, 60px)', fontWeight: 700, color: PALETTE.ink, margin: 0, letterSpacing: '-0.02em', lineHeight: 1.05 }}>
            How Cashel Blue replaced years of spreadsheets<br />
            <em style={{ fontStyle: 'italic', fontWeight: 500, color: PALETTE.magenta }}>in 30 days.</em>
          </h1>
          <p style={{ fontSize: 18, lineHeight: 1.65, color: PALETTE.slate, marginTop: 24, maxWidth: 780 }}>
            Cashel Farmhouse Cheesemakers is a multi-award-winning Tipperary farmhouse dairy with international distribution, family-run, supplying private-label variants to Irish multiple retailers. They built Keystone with us, on their floor, in one of the most operationally complex artisan dairies in the country. Cashel Blue<sup>®</sup> and Crozier Blue<sup>®</sup> are registered trademarks of J&L Grubb Ltd; brand and team references used with the written permission of J&L Grubb Ltd.
          </p>

          {/* Prominent permissions footnote, Item 5 + Item 3 of the
              recalibrated brief. Visible directly under the hero so any
              rights-holder or ASAI review can trace the attribution
              chain without hunting. */}
          <div
            data-testid="cashel-story-permissions-banner"
            style={{
              marginTop: 22,
              padding: '14px 18px',
              background: '#FFF8E7',
              borderLeft: `3px solid ${PALETTE.gold}`,
              fontSize: 13,
              color: PALETTE.ink,
              lineHeight: 1.55,
              maxWidth: 780,
            }}
          >
            <strong>Permission & attribution.</strong>{' '}
            Cashel Blue<sup>®</sup> and Crozier Blue<sup>®</sup> are registered trademarks of J&L Grubb Ltd, used here with written permission granted February 2026. Operational figures cited on this page (production timeline, international distribution footprint, retailer relationships, named quotes) are cited with the permission of Cashel Farmhouse Cheesemakers. A direct reference call can be arranged following an initial discovery call, please{' '}
            <Link to="/site/contact" style={{ color: PALETTE.magenta, fontWeight: 600 }}>request via Contact</Link>. Full attribution trail at the{' '}
            <Link to="/legal/permissions" style={{ color: PALETTE.magenta, fontWeight: 600 }}>permissions log</Link>.
          </div>

          {/* Stats row, operational figures cited with permission
              (see permissions banner above). Specific SKU count and
              market count softened to vague language pending a fresh
              Sarah Furno sign-off on those exact numbers. */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: 18, marginTop: 44 }}>
            {[
              { Icon: Calendar, n: '40+ yrs', l: 'farmhouse production' },
              { Icon: Package, n: 'Extensive', l: 'product range' },
              { Icon: Globe2, n: 'International', l: 'export footprint' },
              { Icon: BarChart3, n: '30 days', l: 'kick-off to live' },
            ].map(({ Icon, n, l }) => (
              <div key={n} style={{ background: '#fff', border: `1px solid ${PALETTE.border}`, borderLeft: `3px solid ${PALETTE.gold}`, padding: '18px 22px' }}>
                <Icon size={18} color={PALETTE.gold} strokeWidth={1.8} />
                <div style={{ fontSize: 22, fontFamily: "'Playfair Display', Georgia, serif", fontWeight: 700, color: PALETTE.ink, marginTop: 8, letterSpacing: '-0.01em' }}>{n}</div>
                <div style={{ fontSize: 11.5, color: PALETTE.muted, marginTop: 2, letterSpacing: '0.04em', textTransform: 'uppercase' }}>{l}</div>
              </div>
            ))}
          </div>

          <div style={{ marginTop: 36, display: 'flex', gap: 14, flexWrap: 'wrap' }}>
            <Link to="/site/contact" style={{ background: PALETTE.ink, color: '#fff', padding: '14px 26px', fontSize: 14, fontWeight: 600, textDecoration: 'none', borderRadius: 4, display: 'inline-flex', alignItems: 'center', gap: 10 }}>
              Book a reference call <ArrowRight size={16} />
            </Link>
            <a href="/case-study/cashel-blue" data-testid="download-case-study-pdf" target="_blank" rel="noopener noreferrer" style={{ background: 'transparent', color: PALETTE.ink, padding: '14px 26px', fontSize: 14, fontWeight: 600, textDecoration: 'none', border: `1px solid ${PALETTE.border}`, borderRadius: 4, display: 'inline-flex', alignItems: 'center' }}>
              Download as PDF
            </a>
          </div>
        </div>
      </section>

      {/* THE PROBLEM */}
      <section style={{ background: '#fff', padding: '90px 32px', borderBottom: `1px solid ${PALETTE.border}` }}>
        <div style={{ maxWidth: 880, margin: '0 auto' }}>
          <div style={{ fontSize: 11, letterSpacing: '0.22em', textTransform: 'uppercase', fontWeight: 700, color: PALETTE.magenta, marginBottom: 14 }}>The problem</div>
          <h2 style={{ fontFamily: "'Playfair Display', Georgia, serif", fontSize: 'clamp(26px, 3.4vw, 38px)', fontWeight: 700, color: PALETTE.ink, margin: 0, letterSpacing: '-0.018em', lineHeight: 1.1 }}>
            Excel built the cheese. Then it started breaking the dispatch.
          </h2>
          <p style={{ fontSize: 16, lineHeight: 1.7, color: PALETTE.slate, marginTop: 22 }}>
            By 2025, Cashel Blue's operation had grown faster than the spreadsheets behind it. 40+ internal SKUs cross-mapped to retailer private-label codes. Variable-weight wheels that didn't reconcile to whole-case orders. Nine export markets, each with its own dispatch-note layout, COA template and pallet-label format. The Excel workbook had become a 28-tab dependency where one wrong reference could brick a Tesco dispatch.
          </p>
          <p style={{ fontSize: 16, lineHeight: 1.7, color: PALETTE.slate, marginTop: 16 }}>
            More importantly, the knowledge sat with one person. When BRCGS mock-recall requirements tightened in 2025, the "what happens when she's on holiday?" question stopped being theoretical.
          </p>
        </div>
      </section>

      {/* THE BUILD */}
      <section style={{ background: PALETTE.paper, padding: '90px 32px', borderBottom: `1px solid ${PALETTE.border}` }}>
        <div style={{ maxWidth: 880, margin: '0 auto' }}>
          <div style={{ fontSize: 11, letterSpacing: '0.22em', textTransform: 'uppercase', fontWeight: 700, color: PALETTE.magenta, marginBottom: 14 }}>The build</div>
          <h2 style={{ fontFamily: "'Playfair Display', Georgia, serif", fontSize: 'clamp(26px, 3.4vw, 38px)', fontWeight: 700, color: PALETTE.ink, margin: 0, letterSpacing: '-0.018em', lineHeight: 1.1 }}>
            We sat on their floor for two days. Then we built.
          </h2>
          <p style={{ fontSize: 16, lineHeight: 1.7, color: PALETTE.slate, marginTop: 22 }}>
            Keystone wasn't designed in a Dublin software office and pitched at Cashel Blue afterwards. It was built side-by-side, on the production floor in Fethard, against a real artisan dairy with a real Excel + macros legacy. Every screen was tested on the same Tuesday-morning dispatch run that had been running for fifteen years.
          </p>

          <ul style={{ listStyle: 'none', padding: 0, margin: '36px 0 0', display: 'grid', gap: 16 }}>
            {[
              { t: 'Week 1, Discovery & data migration', d: 'Two days on the floor mapping the workflow. Five days importing 12 months of historical batches, customers and dispatches from the existing Excel workbook.' },
              { t: 'Week 2, Templates & customer codes', d: 'Per-customer dispatch note templates (Tesco, M&S, Lidl, SuperValu, Centra, Musgrave, Aldi, plus eight export distributors). 40+ SKU → retailer private-label mappings configured.' },
              { t: 'Week 3, Parallel running', d: 'Old system + Keystone running side-by-side. QA Manager training, dispatch ops training, direct line to the build team every working day.' },
              { t: 'Week 4, Go-live', d: 'First full week running dispatch entirely on Keystone. Cut-over support from the build team on every dispatch for the first three days.' },
            ].map((s) => (
              <li key={s.t} style={{ background: '#fff', border: `1px solid ${PALETTE.border}`, borderLeft: `3px solid ${PALETTE.gold}`, padding: '18px 22px', display: 'flex', gap: 16 }}>
                <CheckCircle2 size={20} color={PALETTE.gold} strokeWidth={2} style={{ flexShrink: 0, marginTop: 2 }} />
                <div>
                  <div style={{ fontSize: 15.5, fontWeight: 700, color: PALETTE.ink, marginBottom: 4 }}>{s.t}</div>
                  <div style={{ fontSize: 14, color: PALETTE.muted, lineHeight: 1.6 }}>{s.d}</div>
                </div>
              </li>
            ))}
          </ul>
        </div>
      </section>

      {/* RESULTS */}
      <section style={{ background: '#fff', padding: '90px 32px', borderBottom: `1px solid ${PALETTE.border}` }}>
        <div style={{ maxWidth: 1080, margin: '0 auto' }}>
          <div style={{ fontSize: 11, letterSpacing: '0.22em', textTransform: 'uppercase', fontWeight: 700, color: PALETTE.magenta, marginBottom: 14 }}>The results</div>
          <h2 style={{ fontFamily: "'Playfair Display', Georgia, serif", fontSize: 'clamp(26px, 3.4vw, 38px)', fontWeight: 700, color: PALETTE.ink, margin: 0, letterSpacing: '-0.018em', lineHeight: 1.1 }}>
            Measured. Not vibes.
          </h2>
          <div style={{ marginTop: 32, overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', background: '#fff', border: `1px solid ${PALETTE.border}` }}>
              <thead>
                <tr style={{ background: PALETTE.ink, color: '#fff' }}>
                  <th style={{ padding: '12px 16px', textAlign: 'left', fontSize: 12, letterSpacing: '0.05em', fontWeight: 700 }}>Measure</th>
                  <th style={{ padding: '12px 16px', textAlign: 'left', fontSize: 12, letterSpacing: '0.05em', fontWeight: 700, color: '#94A3B8' }}>Before</th>
                  <th style={{ padding: '12px 16px', textAlign: 'left', fontSize: 12, letterSpacing: '0.05em', fontWeight: 700, background: PALETTE.magenta }}>After</th>
                </tr>
              </thead>
              <tbody>
                {[
                  ['Full-chain traceability lookup', '~2 hours, manual', 'Under 30 seconds, signed PDF'],
                  ['BRCGS mock-recall', 'Half a day, error-prone', '90 seconds'],
                  ['Dispatch note generation', '20 min per dispatch (manual Word)', 'Auto-generated, retailer-formatted'],
                  ['COA per batch', 'Manual copy-paste from Excel', 'Auto-attached on every dispatch'],
                  ['Customer code translation', 'Lookup spreadsheet, error-prone', 'Native bidirectional mapping'],
                  ['Handover risk', 'One person held the knowledge', 'Zero, system holds the workflow'],
                  ['Annual cost of audit prep', '~80 person-hours / year', 'Under 8 person-hours / year'],
                ].map(([m, b, a], i) => (
                  <tr key={m} style={{ background: i % 2 ? PALETTE.cream : '#fff', borderTop: `1px solid ${PALETTE.border}` }}>
                    <td style={{ padding: '12px 16px', fontSize: 13.5, fontWeight: 600, color: PALETTE.ink }}>{m}</td>
                    <td style={{ padding: '12px 16px', fontSize: 13, color: PALETTE.muted }}>✗ {b}</td>
                    <td style={{ padding: '12px 16px', fontSize: 13, color: '#16A34A', fontWeight: 600, background: '#F0FDF4' }}>✓ {a}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </section>

      {/* REFERENCE CALL, no fabricated pull-quote. Real customer voice
          will replace this once the on-record testimonials are gathered. */}
      <section style={{ background: PALETTE.ink, color: '#fff', padding: '90px 32px', borderBottom: `1px solid ${PALETTE.border}` }}>
        <div style={{ maxWidth: 880, margin: '0 auto', textAlign: 'left' }}>
          <div style={{ fontSize: 11, letterSpacing: '0.22em', textTransform: 'uppercase', fontWeight: 700, color: PALETTE.gold, marginBottom: 14 }}>
            Reference call available
          </div>
          <h2 style={{ fontFamily: "'Playfair Display', Georgia, serif", fontSize: 'clamp(24px, 3vw, 34px)', fontWeight: 700, color: '#fff', margin: 0, letterSpacing: '-0.018em', lineHeight: 1.2 }}>
            Rather than paste a marketing quote in someone else&rsquo;s mouth, we&rsquo;d prefer you speak to the team.
          </h2>
          <p style={{ fontSize: 15.5, color: '#CBD5E1', lineHeight: 1.65, marginTop: 20, maxWidth: 680 }}>
            After a short discovery call, we&rsquo;ll broker a direct 20-minute chat with Cashel Blue&rsquo;s QA &amp; Operations team. They&rsquo;ll tell you, in their own words, what works, what doesn&rsquo;t, and how the rollout actually went. Long-form written testimonials are being collected for publication in the coming weeks.
          </p>
        </div>
      </section>

      {/* CTA */}
      <section style={{ background: PALETTE.cream, padding: '80px 32px' }}>
        <div style={{ maxWidth: 1080, margin: '0 auto', textAlign: 'center' }}>
          <h2 style={{ fontFamily: "'Playfair Display', Georgia, serif", fontSize: 'clamp(26px, 3.2vw, 38px)', fontWeight: 700, color: PALETTE.ink, margin: 0, letterSpacing: '-0.018em' }}>
            Want to talk to Cashel Blue's QA team directly?
          </h2>
          <p style={{ fontSize: 16, color: PALETTE.slate, marginTop: 18, maxWidth: 720, margin: '18px auto 0' }}>
            After a brief discovery call, we'll broker a direct intro. No sales pitch, they'll tell you, in their own words, what works and what doesn't.
          </p>
          <Link to="/site/contact" data-testid="cashel-story-cta" style={{ display: 'inline-flex', alignItems: 'center', gap: 10, marginTop: 28, padding: '14px 26px', background: PALETTE.ink, color: '#fff', fontSize: 14, fontWeight: 600, textDecoration: 'none', borderRadius: 4 }}>
            Request the reference call <ArrowRight size={16} />
          </Link>
        </div>
      </section>
    </SiteLayout>
  );
}
